import React, { useState, useEffect } from 'react';
import { Loader2, X, Save, Mail } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { base44 } from '@/api/base44Client';
import { useToast } from '@/components/ui/use-toast';

const inputCls = "bg-[#111115] border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60";
const labelCls = "text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block";

const STATUS_OPTIONS = ['active', 'redeemed', 'expired', 'cancelled'];

export default function GiftCardEditModal({ card, onClose, onSaved }) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    balance: card.balance ?? 0,
    status: card.status || 'active',
    recipient_name: card.recipient_name || '',
    recipient_email: card.recipient_email || '',
    recipient_address_line1: card.recipient_address_line1 || '',
    recipient_address_line2: card.recipient_address_line2 || '',
    recipient_city: card.recipient_city || '',
    recipient_postcode: card.recipient_postcode || '',
    delivery_method: card.delivery_method || 'email',
    gift_message: card.gift_message || '',
    expiry_date: card.expiry_date || '',
    notes: card.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [resendNotice, setResendNotice] = useState(null);

  useEffect(() => {
    if (!resendNotice) return;
    const t = setTimeout(() => setResendNotice(null), 5000);
    return () => clearTimeout(t);
  }, [resendNotice]);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await base44.functions.invoke('adminUpdateGiftCard', {
        id: card.id,
        fields: {
          balance: Number(form.balance),
          status: form.status,
          recipient_name: form.recipient_name,
          recipient_email: form.recipient_email,
          recipient_address_line1: form.recipient_address_line1,
          recipient_address_line2: form.recipient_address_line2,
          recipient_city: form.recipient_city,
          recipient_postcode: form.recipient_postcode,
          delivery_method: form.delivery_method,
          gift_message: form.gift_message,
          expiry_date: form.expiry_date,
          notes: form.notes,
        },
      });
      if (res.data?.success) {
        toast({ title: 'Gift card updated' });
        setDirty(false);
        onSaved?.(res.data.card || { ...card, ...form });
      } else {
        toast({ variant: 'destructive', title: res.data?.error || 'Update failed' });
      }
    } catch (e) {
      toast({ variant: 'destructive', title: 'Update failed' });
    }
    setSaving(false);
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const res = await base44.functions.invoke('adminUpdateGiftCard', {
        id: card.id,
        fields: { recipient_email: form.recipient_email, recipient_name: form.recipient_name },
        resendEmail: true,
      });
      if (res.data?.success && res.data.emailSent) {
        setResendNotice({ type: 'success', message: 'Email resent to ' + form.recipient_email });
      } else {
        setResendNotice({ type: 'error', message: res.data?.emailError || 'Could not send email' });
      }
    } catch (e) {
      setResendNotice({ type: 'error', message: 'Could not send email' });
    }
    setResending(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-[#111115] border border-white/10 w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 sticky top-0 bg-[#111115] z-10">
          <div>
            <p className="text-primary text-[10px] font-bold tracking-widest">EDIT GIFT CARD</p>
            <p className="text-white font-mono font-bold text-sm mt-0.5">{card.code}</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 pb-6 pt-5 space-y-4">
          <div className="flex items-center justify-between bg-white/[0.02] border border-white/5 px-4 py-3">
            <div>
              <p className="text-white/30 text-[10px] font-bold tracking-widest">INITIAL VALUE</p>
              <p className="text-white font-black text-lg">£{Number(card.initial_amount || 0).toFixed(2)}</p>
            </div>
            <div>
              <Label className={labelCls}>REMAINING BALANCE (£)</Label>
              <Input type="number" min={0} step="0.01" value={form.balance}
                onChange={e => set('balance', e.target.value)} className={inputCls + ' w-32'} />
            </div>
          </div>

          <div>
            <Label className={labelCls}>STATUS</Label>
            <div className="grid grid-cols-4 gap-2">
              {STATUS_OPTIONS.map(s => (
                <button key={s} type="button" onClick={() => set('status', s)}
                  className={`py-2.5 text-[10px] font-bold tracking-wider transition-all ${form.status === s ? 'bg-primary text-white' : 'bg-white/5 border border-white/10 text-white/50 hover:border-primary/40'}`}>
                  {s.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className={labelCls}>RECIPIENT NAME</Label>
              <Input value={form.recipient_name} onChange={e => set('recipient_name', e.target.value)} className={inputCls} />
            </div>
            <div>
              <Label className={labelCls}>RECIPIENT EMAIL</Label>
              <Input type="email" value={form.recipient_email} onChange={e => set('recipient_email', e.target.value)} className={inputCls} />
            </div>
          </div>

          <div>
            <Label className={labelCls}>DELIVERY METHOD</Label>
            <div className="grid grid-cols-2 gap-3">
              <button type="button" onClick={() => set('delivery_method', 'email')}
                className={`py-3 text-xs font-bold tracking-wider ${form.delivery_method === 'email' ? 'bg-primary text-white' : 'bg-white/5 border border-white/10 text-white/60'}`}>EMAIL</button>
              <button type="button" onClick={() => set('delivery_method', 'post')}
                className={`py-3 text-xs font-bold tracking-wider ${form.delivery_method === 'post' ? 'bg-primary text-white' : 'bg-white/5 border border-white/10 text-white/60'}`}>POST</button>
            </div>
          </div>

          {form.delivery_method === 'post' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><Label className={labelCls}>ADDRESS LINE 1</Label><Input value={form.recipient_address_line1} onChange={e => set('recipient_address_line1', e.target.value)} className={inputCls} /></div>
              <div className="sm:col-span-2"><Label className={labelCls}>ADDRESS LINE 2</Label><Input value={form.recipient_address_line2} onChange={e => set('recipient_address_line2', e.target.value)} className={inputCls} /></div>
              <div><Label className={labelCls}>CITY</Label><Input value={form.recipient_city} onChange={e => set('recipient_city', e.target.value)} className={inputCls} /></div>
              <div><Label className={labelCls}>POSTCODE</Label><Input value={form.recipient_postcode} onChange={e => set('recipient_postcode', e.target.value)} className={inputCls} /></div>
            </div>
          )}

          <div>
            <Label className={labelCls}>GIFT MESSAGE</Label>
            <textarea value={form.gift_message} onChange={e => set('gift_message', e.target.value)} rows={2}
              className="w-full bg-[#111115] border border-white/10 text-white text-sm px-3 py-2.5 focus:border-primary/60 outline-none resize-none" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label className={labelCls}>EXPIRY DATE</Label><Input type="date" value={form.expiry_date} onChange={e => set('expiry_date', e.target.value)} className={inputCls} /></div>
            <div><Label className={labelCls}>INTERNAL NOTES</Label><Input value={form.notes} onChange={e => set('notes', e.target.value)} className={inputCls} /></div>
          </div>

          <div className="flex gap-3 pt-3 border-t border-white/5">
            <button type="button" onClick={handleResend} disabled={resending || !form.recipient_email}
              className="border border-primary/40 hover:bg-primary/10 text-primary font-bold text-xs tracking-wider py-3 px-5 transition-all disabled:opacity-40 flex items-center gap-2">
              {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} RESEND EMAIL
            </button>
            <button type="button" onClick={onClose}
              className="flex-1 border border-white/10 hover:border-white/30 text-white/50 hover:text-white text-xs font-bold tracking-wider py-3 transition-all">CLOSE</button>
            <button type="button" onClick={handleSave} disabled={saving || !dirty}
              className="flex-1 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider py-3 transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} SAVE
            </button>
          </div>

          {resendNotice && (
            <p className={`text-xs font-bold tracking-wider ${resendNotice.type === 'success' ? 'text-[#00FF00]' : 'text-[#FF0000]'}`}>
              {resendNotice.message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}